<script>
import MixedIcons from '../../../assets/icons/MixedIcons.vue';
import i18n from '@/plugins/i18n';

export default {
    props: {
        Year: {
            type: Number,
            required: true,
        },

        Inputs: {
            type: Object,
            required: true,
        },

        SelectedFacilityId: {
            type: Number,
            required: true,
        },

        GeneralColorCode: {
            type: String,
            required: true,
        },

        ValueType: {
            type: String,
            required: true,
        },
    },

    components: {
        MixedIcons,
    },

    data() {
        return {
            cachedValues: [],
        }
    },

    computed: {
        Input() {
            if (this.isNullish(this.Year) || this.isNullish(this.SelectedFacilityId))
                return null;

            return this.Inputs.find(x => x.facility_id === this.SelectedFacilityId && x.year == this.Year && x.quarter == 5);
        },

        inputType() {
            switch (this.ValueType) {
                case "int":
                    return "number";
                case "float":
                    return "number";
                case "str":
                    return "text";
                default:
                    return "text";
            }
        },
        
        SideClass() {
            if (!this.Input)
                return null;

            return this.Input.has_document ? 'fa-solid fa-check' : null;
        },

        CONTEXT_MENU_ITEMS() {
            return [
                { key: 'cache', text: i18n.t('kpisMetrics.input_field_context_menu_items.cache') },
                { key: 'delete', text: i18n.t('kpisMetrics.input_field_context_menu_items.delete') },
                { key: 'show_details', text: i18n.t('kpisMetrics.input_field_context_menu_items.show_details') },
            ];
        },
    },

    methods: {
        EmitEvent(e, action) {
            if (action === 'create') {
                const event_data = {...this.Input};
                Object.assign(event_data, {
                    create_value: e.target.value, 
                    year: this.Year, 
                    quarter: 5,        
                });

                this.$emit(action, event_data);
            } else {
                this.$emit(action, this.Input);
            }
        },

        async handleInput(e) {
            if (this.inputType === 'number') {
                const sanitized = await this.sanitizeNumberInput(e);
                e.target.value = sanitized;
            }
        },

        async sanitizeNumberInput(e) {
            let val = String(e.target.value);

            const lastDot = val.lastIndexOf('.');
            const lastComma = val.lastIndexOf(',');
            const lastSep = Math.max(lastDot, lastComma);


            if (lastSep !== -1) {
                let intPart = val.slice(0, lastSep).replace(/[.,]/g, '');
                let decPart = val.slice(lastSep + 1).replace(/[.,]/g, '');
                val = intPart + '.' + decPart;
            } else {
                val = val.replace(/[.,]/g, '');
            }

            return String(val);
        },

        HandleContextMenuAction(action) {
            if (action === 'delete') {
                this.EmitEvent(null, 'delete');
            } else if (action === 'cache') {
                this.revertToCache();
            } else if (action === 'show_details' && this.Input) {
                this.EmitEvent(this.Input.metrics_input_table_id, 'show_details');
            }
        },

        cacheValue(e) {
            this.cachedValues.push(e.target.value);
        },

        revertToCache() {
            const value = this.getCachedValue();
            if (!value) {
                this.$notify('warning', i18n.t('kpisMetrics.cache_value_warning'), true);
                return;
            }

            const el = document.getElementById(`update-field-${this.Input.metrics_input_table_id}`);
            el.value = value;
            el.dispatchEvent(new Event('change'));
        },

        getCachedValue() {
            return this.cachedValues.pop();
        },

        isNullish(val) {
            return val === null || val === undefined;
        },
    }
}
</script>

<template>
    <div class="metric-input-field">
        <input v-if="Input" 
            :id="`update-field-${Input.metrics_input_table_id}`"
            type="text"
            v-model="Inputs.find(x => x.metrics_input_table_id === Input.metrics_input_table_id).value"
            @input="handleInput($event)"
            @change="EmitEvent($event, 'update')"
            @focus="cacheValue($event)"
        >
        
        <input v-else
            type="text"
            @input="handleInput($event)"
            @change="EmitEvent($event, 'create')"
        >

        <button class="icon-button" style="padding: 0 0 0 .25em;"
            v-context-menu="{ items: CONTEXT_MENU_ITEMS, onItemClick: HandleContextMenuAction, data: Input, eventType: 'click', position: 'top' }"
        >
            <i class="fa-solid fa-ellipsis-vertical"></i>
        </button>
    </div>
</template>

<style lang='scss' scoped>
.metric-input-field {
    width: 100%;
    display: flex;
    flex-direction: row;
    place-items: center;
    
    > input {
        text-align: center;
        border-radius: 5px;
        border: thin solid var(--sustainfinity-navy);
        outline: 0;
        background: 0;
        width: 100%;
        &:focus {
            border: thin solid var(--sustainfinity-blue);
        }
    }

    > .icon-button {
        &:focus {
            outline: 0 !important;
            border: 0 !important;
        }
    }
}
</style>